import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Bridges Turbo's global form-confirmation hook to the shared styled modal.
// On connect it points `Turbo.config.forms.confirm` at this instance, so every
// control carrying `data-turbo-confirm` opens this dialog and awaits the
// returned Promise<boolean> (true submits, false aborts) instead of the native
// window.confirm() popup, which stamps the origin hostname on top and can't be
// styled. The modal is rendered once in the layout, so exactly one instance
// owns the hook; each full page render reconnects and re-registers it.
//
// Turbo hands the hook (message, element, submitter). The message is the only
// part of the copy it knows about; the action's title, its verb, and whether
// it's destructive ride along as data attributes written by the `confirm_data`
// helper — a confirmation must name the action it confirms, and only the call
// site knows the action (docs/specs/confirm-dialogs.md). They can sit on
// either the form or the submitter depending on how the call site spells
// `data:`, so both are consulted, submitter first.
export default class extends Controller {
  static targets = ["modal", "title", "message", "submit", "submitLabel"]
  static values = { submitClass: String, destructiveClass: String }

  connect() {
    Turbo.config.forms.confirm = (message, element, submitter) =>
      this.confirm(message, element, submitter)
  }

  confirm(message, element, submitter) {
    // showModal() throws if the dialog is already open; a confirmation already
    // in flight means a second one can't be answered, so decline it.
    if (this.modalTarget.open) return Promise.resolve(false)

    const read = (name) => submitter?.dataset?.[name] ?? element?.dataset?.[name] ?? ""
    const destructive = read("turboConfirmDestructive") === "true"

    this.titleTarget.textContent = read("turboConfirmTitle")
    this.messageTarget.textContent = message
    this.submitLabelTarget.textContent = read("turboConfirmVerb")
    this.submitTarget.className = destructive
      ? this.destructiveClassValue
      : this.submitClassValue

    this.result = false
    this.modalTarget.showModal()
    return new Promise((resolve) => {
      this.resolvePromise = resolve
    })
  }

  accept() {
    this.result = true
    this.modalTarget.close()
  }

  decline() {
    // A declined confirmation means nothing was submitted. Say so, because a
    // dirty-guard released itself the moment the submit event fired and would
    // otherwise stay released for the rest of the page's life.
    document.dispatchEvent(new CustomEvent("cup:confirm-declined"))
    this.modalTarget.close()
  }

  backdropClick(event) {
    if (event.target === this.modalTarget) this.modalTarget.close()
  }

  // Fires for every close path — accept, decline, Esc, backdrop — so no route
  // leaves the awaiting form submission hanging on an unresolved Promise.
  closed() {
    if (!this.resolvePromise) return

    this.resolvePromise(this.result)
    this.resolvePromise = null
  }
}
